import { QuestStep, QUEST_STEP_IDS } from '@/types/quest-types';

export const QUEST_STEPS: Record<number, QuestStep> = {
  [QUEST_STEP_IDS.CREATE_PAGE]: {
    id: QUEST_STEP_IDS.CREATE_PAGE,
    title: 'Create Document',
    description: 'Learn how to create documents to organize your ideas and work.',
    page: '/private-text-generator',
    target: '[data-test-id="empty-state-create-page-button"]',
    completionAction: 'Never stare at a blank document again - let AI jumpstart your creativity',
    celebrationTitle: 'Document Created! 📄',
    celebrationDescription: 'Excellent! You\'ve created your first document. Documents are the foundation of organizing your work.',
    nextStepHint: 'Next: Learn how to research any topic instantly'
  },
  
  [QUEST_STEP_IDS.GENERATE_RESEARCH]: {
    id: QUEST_STEP_IDS.GENERATE_RESEARCH,
    title: 'Generate Research',
    description: 'Discover how to generate comprehensive research on any topic in minutes.',
    page: '/private-research-generator',
    target: '.research-generator-sidebar',
    completionAction: 'Skip hours of googling - get expert-level insights instantly',
    celebrationTitle: 'Research Generated! 🔬',
    celebrationDescription: 'Fantastic! You\'ve learned how to generate in-depth research instantly.',
    nextStepHint: 'Next: Create stunning images with AI'
  },
  
  [QUEST_STEP_IDS.GENERATE_IMAGE]: {
    id: QUEST_STEP_IDS.GENERATE_IMAGE,
    title: 'Generate Image',
    description: 'Learn to create beautiful AI-generated images for your projects.',
    page: '/private-image-generator',
    target: '[data-test-id="generate-image-button"]',
    completionAction: 'Create professional visuals without design skills or stock photo costs',
    celebrationTitle: 'Image Created! 🎨',
    celebrationDescription: 'Amazing! You\'ve mastered AI image generation. Your creativity knows no bounds now.',
    nextStepHint: 'Next: Organize everything with projects'
  },
  
  [QUEST_STEP_IDS.CREATE_PROJECT]: {
    id: QUEST_STEP_IDS.CREATE_PROJECT,
    title: 'Create Project',
    description: 'Learn how to organize your work with projects that bring everything together.',
    page: '/project-manager',
    target: '[data-test-id="create-project-button"]',
    completionAction: 'Stop losing track of work - keep everything organized in one place',
    celebrationTitle: 'Project Created! 📁',
    celebrationDescription: 'Brilliant! Projects help you organize documents, research, images, and tasks in one place.',
    nextStepHint: 'Next: Add your first task to stay organized'
  },
  
  [QUEST_STEP_IDS.ADD_TASK]: {
    id: QUEST_STEP_IDS.ADD_TASK,
    title: 'Add Task',
    description: 'Let AI break down your work into actionable tasks and handle the details.',
    page: '/project/:id/tasks',
    target: '[data-test-id="add-task-button"]',
    completionAction: 'Stop planning manually - let AI recommend and create your tasks',
    celebrationTitle: 'Task Added! ✅',
    celebrationDescription: 'Perfect! AI has generated smart tasks to help you stay organized and productive.',
    nextStepHint: 'Next: Import documents to your project'
  },
  
  [QUEST_STEP_IDS.IMPORT_PAGE]: {
    id: QUEST_STEP_IDS.IMPORT_PAGE,
    title: 'Import Document',
    description: 'Learn how to bring existing documents into your project workspace.',
    page: '/project/:id/pages',
    target: '[data-test-id="import-page-button"]',
    completionAction: 'Connect your scattered documents - build comprehensive project resources',
    celebrationTitle: 'Document Imported! 📋',
    celebrationDescription: 'Great! You can now import any document into your projects for better organization.',
    nextStepHint: 'Next: Import research to enhance your project'
  },
  
  [QUEST_STEP_IDS.IMPORT_RESEARCH]: {
    id: QUEST_STEP_IDS.IMPORT_RESEARCH,
    title: 'Import Research',
    description: 'Learn how to add research resources to your project for easy access.',
    page: '/project/:id/resources',
    target: '[data-test-id="import-research-button"]',
    completionAction: 'Build your knowledge base - make research accessible when you need it',
    celebrationTitle: 'Research Imported! 📚',
    celebrationDescription: 'Excellent! Research resources are now organized within your project.',
    nextStepHint: 'Next: Collaborate with team members'
  },
  
  [QUEST_STEP_IDS.ADD_TEAM_MEMBER]: {
    id: QUEST_STEP_IDS.ADD_TEAM_MEMBER,
    title: 'Add Team Member',
    description: 'Share your AI-generated content and collaborate with others on projects.',
    page: '/project/:id/settings',
    target: '[data-test-id="assign-task-button"]',
    completionAction: 'Share your AI content with others - collaborate and multiply your impact',
    celebrationTitle: 'Team Member Added! 👥',
    celebrationDescription: 'Outstanding! You\'ve completed your onboarding journey. You\'re now ready to use all features!',
    nextStepHint: 'Final step: Celebrate your achievement!'
  },
  
  // Celebration step (step 9)
  9: {
    id: 9,
    title: 'Quest Complete! 🎉',
    description: 'Congratulations! You\'ve mastered all the core features and are ready to build amazing projects.',
    page: '', // No specific page - celebration can be shown anywhere
    target: '',
    completionAction: 'You\'re now a power user! Start creating incredible content with AI assistance.',
    celebrationTitle: 'Welcome to the Community! 🚀',
    celebrationDescription: 'You\'ve completed your onboarding quest and unlocked the full potential of our platform. Happy creating!',
    nextStepHint: ''
  }
};

export const TOTAL_QUEST_STEPS = 8; // Only count the actual quest steps, not the celebration step

export const getQuestStep = (stepId: number): QuestStep | null => {
  return QUEST_STEPS[stepId] || null;
};

export const getNextQuestStep = (currentStep: number): QuestStep | null => {
  const nextStepId = currentStep + 1;
  return getQuestStep(nextStepId);
};

export const isQuestCompleted = (completedStep: number): boolean => {
  return completedStep >= TOTAL_QUEST_STEPS;
};